
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-body-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-[#6931F8] text-white hover:bg-[#6931F8]/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-red-100 text-red-700 hover:bg-red-200",
        outline: "text-foreground",
        success: 
          "border-transparent bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300",
        warning: 
          "border-transparent bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300",
        info: 
          "border-transparent bg-[#EAE2FE] text-[#6931F8] dark:bg-[#6931F8]/20 dark:text-[#EAE2FE]",
        purple:
          "border-transparent bg-[#D6BCFA] text-[#4A36A3] hover:bg-[#D6BCFA]/80",
        customer:
          "border-transparent bg-[#D6BCFA] text-[#4A36A3] hover:bg-[#D6BCFA]/80",
        supplier:
          "border-transparent bg-[#FFFAE0] text-[#75622C] hover:bg-[#FFFAE0]/80",
        draft: 
          "border-transparent bg-[#F8F8F8] text-gray-600 dark:bg-gray-800/30 dark:text-gray-400",
        pending: 
          "border-transparent bg-[#FFF8E6] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
        paid: 
          "border-transparent bg-[#E6FFF0] text-green-700 dark:bg-green-900/20 dark:text-green-300",
        overdue: 
          "border-transparent bg-[#FFE6E6] text-red-700 dark:bg-red-900/20 dark:text-red-300",
        planning:
          "border-transparent bg-[#EAE2FE] text-[#6931F8] dark:bg-[#6931F8]/20 dark:text-[#EAE2FE]",
        ongoing:
          "border-transparent bg-[#E6FFF0] text-green-700 dark:bg-green-900/20 dark:text-green-300",
        completed:
          "border-transparent bg-[#F8F8F8] text-gray-600 dark:bg-gray-800/30 dark:text-gray-400",
        paused:
          "border-transparent bg-[#FFF8E6] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
        blue: 
          "border-transparent bg-[#6931F8] text-white hover:bg-[#6931F8]/80",
        light: 
          "border border-gray-200 bg-white text-gray-700 shadow-sm",
        accent: 
          "border-transparent bg-[#F9F9FB] text-gray-600 hover:bg-gray-100",
        pricing:
          "border-transparent bg-[#F9F9FB] border border-gray-200 text-gray-700 font-semibold",
        discount:
          "border-transparent bg-[#F0EBFF] text-[#6931F8] font-medium px-4 py-1.5 text-xs",
        popular:
          "border-transparent bg-[#4A1D96] text-white font-medium px-4 py-1.5 text-xs",
        label:
          "relative border border-gray-200 bg-white text-gray-700 font-medium px-6 py-2 text-sm shadow-sm hover:shadow-md hover:bg-gray-50 transition-all duration-300 pl-8 before:content-[''] before:absolute before:left-3 before:top-1/2 before:-translate-y-1/2 before:w-3 before:h-3 before:rounded-full before:bg-[#6931F8] before:shadow-sm",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
